import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/CloudUpload";
import AssignmentIcon from "@mui/icons-material/Description";
import ArchiveIcon from "@mui/icons-material/Archive";
import WindowOutlinedIcon from "@mui/icons-material/WindowOutlined";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

const AppHeader = ({
  isMobile = false,
  columnCount = 3,
  onColumnCountChange = () => {},
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const displayName = user && user.username;
  const initialsFrom = (text) => {
    if (!text) return "U";
    const parts = String(text).trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };
  const initials = initialsFrom(displayName);
  const [loginUser, setloginUser] = useState(null);
  const userMenuOpen = Boolean(loginUser);

  const openUserMenu = (e) => setloginUser(e.currentTarget);
  const closeUserMenu = () => setloginUser(null);
  const [features, setFeatures] = useState(null);
  const featureMenuOpen = Boolean(features);

  const openFeatureMenu = (e) => setFeatures(e.currentTarget);
  const closeFeatureMenu = () => setFeatures(null);
  const handleFeatureNavigate = (path) => {
    navigate(path);
    closeFeatureMenu();
  };

  const gridIconStyle = (active) => ({
    backgroundColor: active ? "#E5EDFF" : "transparent",
    color: active ? "#1F60C0" : "#4B5563",
    borderRadius: "8px",
    padding: 1,
    marginRight: "6px",
    "&:hover": {
      backgroundColor: "#E5EDFF",
      color: "#1F60C0",
    },
  });

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: "100%",
        bgcolor: "#fff",
        borderBottom: "1px solid #E5E7EB",
        boxShadow: "none",
        zIndex: (t) => t.zIndex.drawer + 1,
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          px: 3,
          height: { xs: 56, sm: 64, md: 70 },
          gap: 2,
        }}
      >
        <Box
          component={Link}
          to="/"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            textDecoration: "none",
            mr: 2,
          }}
        >
          <img
            src="./images/cognecto_logo.svg"
            alt="cognecto"
            width={180}
            height={180}
          />
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            id="features-button"
            aria-controls={featureMenuOpen ? "features-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={featureMenuOpen ? "true" : undefined}
            variant="contained"
            onClick={openFeatureMenu}
            startIcon={<DashboardOutlinedIcon />}
            endIcon={<KeyboardArrowDownIcon />}
            size="small"
            sx={{
              textTransform: "none",
              background: featureMenuOpen ? "#E5E7EB" : "#F3F4F6",
              color: "#374151",
              fontWeight: 600,
              borderRadius: "8px",
              px: 2,
              boxShadow: "none",
              "&:hover": { background: "#E5E7EB" },
            }}
          >
            features
          </Button>
          <Menu
            id="features-menu"
            anchorEl={features}
            open={featureMenuOpen}
            onClose={closeFeatureMenu}
            PaperProps={{
              elevation: 4,
              sx: {
                minWidth: 180,
                borderRadius: 2,
                mt: 1,
                "& .MuiMenuItem-root": {
                  px: 2,
                  py: 1,
                  gap: 1.5,
                },
              },
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem onClick={() => handleFeatureNavigate("/upload")}>
              <ListItemIcon sx={{ minWidth: 0 }}>
                <UploadIcon fontSize="small" sx={{ color: "#7C3AED" }} />
              </ListItemIcon>
              <ListItemText primary="Upload" />
            </MenuItem>

            <MenuItem onClick={() => handleFeatureNavigate("/reports")}>
              <ListItemIcon sx={{ minWidth: 0 }}>
                <AssignmentIcon fontSize="small" sx={{ color: "#16A34A" }} />
              </ListItemIcon>
              <ListItemText primary="Reports" />
            </MenuItem>

            <MenuItem onClick={() => handleFeatureNavigate("/archive")}>
              <ListItemIcon sx={{ minWidth: 0 }}>
                <ArchiveIcon fontSize="small" sx={{ color: "#B45309" }} />
              </ListItemIcon>
              <ListItemText primary="Archive" />
            </MenuItem>
          </Menu>

          {!isMobile && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                borderLeft: "1px solid #E5E7EB",
                pl: 2,
                height: "32px",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#6b7280", mr: 1 }}
              >
                Grid:
              </Typography>
              <IconButton
                onClick={() => onColumnCountChange(2)}
                sx={gridIconStyle(columnCount === 2)}
                size="small"
              >
                <WindowOutlinedIcon />
              </IconButton>
              <IconButton
                onClick={() => onColumnCountChange(3)}
                sx={gridIconStyle(columnCount === 3)}
                size="small"
              >
                <GridOnOutlinedIcon />
              </IconButton>
            </Box>
          )}
          <Box sx={{ ml: 0.5 }}>
            <IconButton
              onClick={openUserMenu}
              size="small"
              sx={{
                p: 0,
                borderRadius: "50%",
                border: userMenuOpen
                  ? "2px solid #E5E7EB"
                  : "2px solid transparent",
              }}
              aria-controls={userMenuOpen ? "user-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={userMenuOpen ? "true" : undefined}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: "#1F60C0",
                  fontSize: "1rem",
                  fontWeight: 600,
                }}
              >
                {initials}
              </Avatar>
            </IconButton>
            <Menu
              id="user-menu"
              anchorEl={loginUser}
              open={userMenuOpen}
              onClose={closeUserMenu}
              PaperProps={{
                elevation: 6,
                sx: {
                  minWidth: 200,
                  borderRadius: 1.5,
                  overflow: "hidden",
                  mt: 1,
                },
              }}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                disabled
                sx={{ px: 2, py: 1.5, opacity: "1 !important" }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: "#111827" }}
                  >
                    {displayName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#6B7280" }}>
                    {user?.email || "User Account"}
                  </Typography>
                </Box>
              </MenuItem>
              <Divider sx={{ my: 0 }} />
              <MenuItem sx={{ px: 2, py: 1.5 }} onClick={closeUserMenu}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <PersonOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Profile" />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  Promise.resolve(logout && logout()).then(() => {
                    closeUserMenu();
                    navigate("/login");
                  });
                }}
                sx={{ px: 2, py: 1.5, color: "#DC2626" }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <LogoutIcon fontSize="small" sx={{ color: "#DC2626" }} />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};
export default AppHeader;
